import { useState, useEffect, useCallback, useRef } from 'react';

export const useEncounterFormState = (encounter, mode = 'edit') => {
  const [formData, setFormData] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [careTeamData, setCareTeamData] = useState(null);
  
  // Refs to prevent infinite loops
  const hasMountedRef = useRef(false);
  const lastEncounterIdRef = useRef(null);

  // Initialize formData from encounter when loaded
  useEffect(() => {
    if (encounter && lastEncounterIdRef.current !== encounter.id) {
      lastEncounterIdRef.current = encounter.id;
      
      const newFormData = {
        ...encounter,
        vitals: encounter.vitals || {
          bloodPressure: '',
          heartRate: '',
          temperature: '',
          respiratoryRate: '',
          oxygenSaturation: '',
          weight: '',
          height: '',
          bmi: ''
        },
        notes: {
          consultationNotes: encounter.notes?.consultationNotes || '',
          progressNotes: encounter.notes?.progressNotes || '',
          nurseNotes: encounter.notes?.nurseNotes || '',
          procedureNotes: encounter.notes?.procedureNotes || '',
          soapNotes: {
            subjective: encounter.notes?.soapNotes?.subjective || '',
            objective: encounter.notes?.soapNotes?.objective || '',
            assessment: encounter.notes?.soapNotes?.assessment || '',
            plan: encounter.notes?.soapNotes?.plan || ''
          },
          simpleNotes: encounter.notes?.simpleNotes || '',
          phoneNotes: encounter.notes?.phoneNotes || ''
        },
        symptoms: encounter.symptoms || [],
        actionPlan: encounter.actionPlan || '',
        provider: encounter.provider || {},
        department: encounter.department || '',
        unit: encounter.unit || '',
        chiefComplaint: encounter.chiefComplaint || '',
        initialAssessment: encounter.initialAssessment || '',
        // Ensure bed/room data is properly mapped to formData
        room: encounter.room || encounter.inpatientDetails?.roomNumber || '',
        roomId: encounter.roomId || '',
        bed: encounter.bed || encounter.inpatientDetails?.bedNumber || '',
        bedId: encounter.bedId || '',
        inpatientDetails: {
          admissionType: encounter.inpatientDetails?.admissionType || '',
          admittingDiagnosis: encounter.inpatientDetails?.admittingDiagnosis || '',
          dischargeDisposition: encounter.inpatientDetails?.dischargeDisposition || '',
          lengthOfStay: encounter.inpatientDetails?.lengthOfStay || '',
          roomNumber: encounter.room || encounter.inpatientDetails?.roomNumber || '',
          bedNumber: encounter.bed || encounter.inpatientDetails?.bedNumber || '',
          attendingPhysician: encounter.inpatientDetails?.attendingPhysician || '',
          admittingPhysician: encounter.inpatientDetails?.admittingPhysician || '',
          primaryCarePhysician: encounter.inpatientDetails?.primaryCarePhysician || ''
        }
      };
      
      setFormData(prevFormData => {
        if (!prevFormData || prevFormData.id !== encounter.id) {
          return newFormData;
        }
        return prevFormData;
      });
    }
  }, [encounter?.id, encounter]);

  // Initialize initialFormData when formData is first set
  useEffect(() => {
    if (formData && !initialFormData && hasMountedRef.current) {
      setInitialFormData(JSON.parse(JSON.stringify(formData)));
    }
  }, [formData, initialFormData]);

  // Track pending changes, but exclude changes due to external data refresh
  useEffect(() => {
    if (mode === 'edit' && initialFormData && formData && hasMountedRef.current) {
      const formDataChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
      const careTeamChanged = careTeamData !== null;
      const newHasPendingChanges = formDataChanged || careTeamChanged;
      
      setHasPendingChanges(prev => {
        if (prev !== newHasPendingChanges) {
          return newHasPendingChanges;
        }
        return prev;
      });
    }
  }, [formData, careTeamData, initialFormData, mode]);

  // Update initialFormData when encounter is refreshed externally (e.g., bed assignment)
  useEffect(() => {
    if (mode === 'edit' && formData && initialFormData && encounter && hasMountedRef.current) {
      // Check if the change is likely due to external encounter refresh (bed/room assignment)
      const currentLocationInfo = {
        room: formData.room,
        roomId: formData.roomId,
        bed: formData.bed,
        bedId: formData.bedId,
        department: formData.department,
        departmentId: formData.departmentId,
        departmentCode: formData.departmentCode,
        // Include nested encounter-type-specific location fields
        inpatientDetailsRoomNumber: formData.inpatientDetails?.roomNumber,
        inpatientDetailsBedNumber: formData.inpatientDetails?.bedNumber,
        observationDetailsRoomNumber: formData.observationDetails?.roomNumber,
        observationDetailsBedNumber: formData.observationDetails?.bedNumber,
        emergencyDetailsRoomNumber: formData.emergencyDetails?.roomNumber,
        emergencyDetailsBedNumber: formData.emergencyDetails?.bedNumber,
        outpatientDetailsRoomNumber: formData.outpatientDetails?.roomNumber,
        outpatientDetailsBedNumber: formData.outpatientDetails?.bedNumber
      };
      
      const initialLocationInfo = {
        room: initialFormData.room,
        roomId: initialFormData.roomId,
        bed: initialFormData.bed,
        bedId: initialFormData.bedId,
        department: initialFormData.department,
        departmentId: initialFormData.departmentId,
        departmentCode: initialFormData.departmentCode,
        // Include nested encounter-type-specific location fields
        inpatientDetailsRoomNumber: initialFormData.inpatientDetails?.roomNumber,
        inpatientDetailsBedNumber: initialFormData.inpatientDetails?.bedNumber,
        observationDetailsRoomNumber: initialFormData.observationDetails?.roomNumber,
        observationDetailsBedNumber: initialFormData.observationDetails?.bedNumber,
        emergencyDetailsRoomNumber: initialFormData.emergencyDetails?.roomNumber,
        emergencyDetailsBedNumber: initialFormData.emergencyDetails?.bedNumber,
        outpatientDetailsRoomNumber: initialFormData.outpatientDetails?.roomNumber,
        outpatientDetailsBedNumber: initialFormData.outpatientDetails?.bedNumber
      };
      
      const locationChanged = JSON.stringify(currentLocationInfo) !== JSON.stringify(initialLocationInfo);
      
      // If only location information changed, update initialFormData to prevent false "unsaved changes"
      if (locationChanged) {
        const otherDataCopy = { ...formData };
        delete otherDataCopy.room;
        delete otherDataCopy.roomId;
        delete otherDataCopy.bed;
        delete otherDataCopy.bedId;
        delete otherDataCopy.department;
        delete otherDataCopy.departmentId;
        delete otherDataCopy.departmentCode;
        // Also exclude nested location fields from comparison
        if (otherDataCopy.inpatientDetails) {
          const inpatientDetailsCopy = { ...otherDataCopy.inpatientDetails };
          delete inpatientDetailsCopy.roomNumber;
          delete inpatientDetailsCopy.bedNumber;
          otherDataCopy.inpatientDetails = inpatientDetailsCopy;
        }
        if (otherDataCopy.observationDetails) {
          const observationDetailsCopy = { ...otherDataCopy.observationDetails };
          delete observationDetailsCopy.roomNumber;
          delete observationDetailsCopy.bedNumber;
          otherDataCopy.observationDetails = observationDetailsCopy;
        }
        if (otherDataCopy.emergencyDetails) {
          const emergencyDetailsCopy = { ...otherDataCopy.emergencyDetails };
          delete emergencyDetailsCopy.roomNumber;
          delete emergencyDetailsCopy.bedNumber;
          otherDataCopy.emergencyDetails = emergencyDetailsCopy;
        }
        if (otherDataCopy.outpatientDetails) {
          const outpatientDetailsCopy = { ...otherDataCopy.outpatientDetails };
          delete outpatientDetailsCopy.roomNumber;
          delete outpatientDetailsCopy.bedNumber;
          otherDataCopy.outpatientDetails = outpatientDetailsCopy;
        }
        
        const initialOtherDataCopy = { ...initialFormData };
        delete initialOtherDataCopy.room;
        delete initialOtherDataCopy.roomId;
        delete initialOtherDataCopy.bed;
        delete initialOtherDataCopy.bedId;
        delete initialOtherDataCopy.department;
        delete initialOtherDataCopy.departmentId;
        delete initialOtherDataCopy.departmentCode;
        // Also exclude nested location fields from comparison
        if (initialOtherDataCopy.inpatientDetails) {
          const inpatientDetailsCopy = { ...initialOtherDataCopy.inpatientDetails };
          delete inpatientDetailsCopy.roomNumber;
          delete inpatientDetailsCopy.bedNumber;
          initialOtherDataCopy.inpatientDetails = inpatientDetailsCopy;
        }
        if (initialOtherDataCopy.observationDetails) {
          const observationDetailsCopy = { ...initialOtherDataCopy.observationDetails };
          delete observationDetailsCopy.roomNumber;
          delete observationDetailsCopy.bedNumber;
          initialOtherDataCopy.observationDetails = observationDetailsCopy;
        }
        if (initialOtherDataCopy.emergencyDetails) {
          const emergencyDetailsCopy = { ...initialOtherDataCopy.emergencyDetails };
          delete emergencyDetailsCopy.roomNumber;
          delete emergencyDetailsCopy.bedNumber;
          initialOtherDataCopy.emergencyDetails = emergencyDetailsCopy;
        }
        if (initialOtherDataCopy.outpatientDetails) {
          const outpatientDetailsCopy = { ...initialOtherDataCopy.outpatientDetails };
          delete outpatientDetailsCopy.roomNumber;
          delete outpatientDetailsCopy.bedNumber;
          initialOtherDataCopy.outpatientDetails = outpatientDetailsCopy;
        }
        
        const onlyLocationChanged = JSON.stringify(otherDataCopy) === JSON.stringify(initialOtherDataCopy);
        
        if (onlyLocationChanged) {
          console.log('[useEncounterFormState] Location-only change detected, updating initialFormData');
          setInitialFormData(prev => {
            const newData = JSON.parse(JSON.stringify(formData));
            if (JSON.stringify(prev) !== JSON.stringify(newData)) {
              return newData;
            }
            return prev;
          });
        }
      }
    }
  }, [formData, initialFormData, mode, encounter]);

  // Memoize the reset function to prevent unnecessary re-renders
  const resetFunction = useCallback(() => {
    setHasPendingChanges(false);
    setCareTeamData(null);
    if (formData) {
      setInitialFormData(JSON.parse(JSON.stringify(formData)));
    }
  }, [formData]);

  // Set mounted flag
  useEffect(() => {
    hasMountedRef.current = true;
    return () => {
      hasMountedRef.current = false;
    };
  }, []);

  return {
    formData,
    setFormData,
    hasPendingChanges,
    careTeamData,
    setCareTeamData,
    resetFunction,
  };
}; 